import customParseFormat from 'dayjs/plugin/customParseFormat';

import dayjs from '@/utils/dayjs';

dayjs.extend(customParseFormat);

import type { VersionMetadata } from '@/typings/version';

/**
 * Parse version filename with format DATE_VERSION.md
 * Example: 2025-12-11_1.2.3.md
 */
export function parseVersionFilename(filename: string): VersionMetadata | null {
  const nameWithoutExt = filename.replace(/\.md$/, '');
  const [date, version] = nameWithoutExt.split('_');

  // Validate format: DATE_VERSION
  if (!date || !version) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid filename format: ${filename}`);
    }
    return null;
  }

  // Validate date format (YYYY-MM-DD)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid date format: ${date}. Expected YYYY-MM-DD`);
    }
    return null;
  }

  // Validate version contains dots (e.g., 1.2.3)
  if (!version.includes('.')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid version format: ${version}`);
    }
    return null;
  }

  return { version, date, filename };
}

/**
 * Compare semantic versions (e.g., "3.12.01" vs "3.11.99")
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  const maxLength = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < maxLength; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;

    if (num1 !== num2) return num1 > num2 ? 1 : -1;
  }

  return 0;
}

/**
 * Format date from YYYY-MM-DD to readable Portuguese format
 * Example: "2025-12-11" → "11 de dezembro de 2025"
 */
export function formatVersionDate(dateString: string): string {
  const date = dayjs(dateString, 'YYYY-MM-DD');

  if (!date.isValid()) return dateString;

  return date.format('D [de] MMMM [de] YYYY');
}

/**
 * Check if version is newer than last seen version
 */
export function isVersionNewer(version: string, lastSeen: string | null): boolean {
  return lastSeen === null || compareVersions(version, lastSeen) > 0;
}

/**
 * Filter markdown content by user role
 * Removes role-specific sections that don't match the user's role
 *
 * Format in markdown:
 * <!-- ROLE:admin -->
 * Admin-only content
 * <!-- /ROLE -->
 *
 * <!-- ROLE:admin:user -->
 * Content for both admin and user
 * <!-- /ROLE -->
 *
 * @param markdown - The markdown content to filter
 * @param userRole - The user's role (e.g., 'admin', 'user')
 * @returns Filtered markdown with only relevant sections
 *
 * @example
 * const filtered = filterContentByRole(markdown, 'admin');
 * // Returns markdown with admin sections visible, others removed
 */
export function filterContentByRole(markdown: string, userRole?: string): string {
  if (!userRole) {
    // If no role, remove all role-specific sections
    return markdown.replace(/<!-- ROLE:.*? -->[\s\S]*?<!-- \/ROLE -->/g, '');
  }

  return markdown.replace(
    /<!-- ROLE:(.*?) -->([\s\S]*?)<!-- \/ROLE -->/g,
    (_match, rolesString, content) => {
      // Split roles by colon (e.g., "admin:user" => ["admin", "user"])
      const roles = rolesString.split(':').map((r: string) => r.trim());
      // Keep content if user's role is in the list
      return roles.includes(userRole) ? content : '';
    },
  );
}
